import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules } from '@utils';
import { useDefaultCurrency, useUpdateAccountsLimits, useNavigate } from '@utils/hooks';

import { accountsActions, notificationsActions, useStore } from '@modules';
import { AccountsOperationsEnum } from '@modules/accounts/state';
import { AccountsSellRateResponse, PaymentAliasEnum } from '@modules/accounts/types';
import { CurrencyEnum } from '@modules/global/types';
import { RateTypesEnum } from '@modules/wallet/types';

import ButtonOld from '@components/ButtonOld';
import ConverterOld, { TConverterOld } from '@components/ConverterOld';
import FeeInformer from '@components/FeeInformer';
import RateInformer from '@containers/RateInformer';
import LoaderLayout from '@layouts/LoaderLayout';

import style from './AccountsSell.scss';

const cn = bemCssModules(style, 'AccountsSell');

export type TAccountsSell = {
  paymentAlias: PaymentAliasEnum;
};

export const AccountsSell: CustomFC<TAccountsSell> = ({ className, paymentAlias }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [{ accounts, global }, dispatch] = useStore();
  const { currencies } = global;

  const displayOptions = currencies.config.display_options;
  const fiatCurrency = CONST.accountsCurrency;
  const cryptoCurrency = useDefaultCurrency();
  const [currentState, setCurrentState] = useState<{
    from?: CurrencyEnum;
    to?: CurrencyEnum;
    amount?: string;
  }>({});

  const [fromCurrency, setFromCurrency] = useState<CurrencyEnum>(cryptoCurrency);
  const [toCurrency, setToCurrency] = useState<CurrencyEnum>(fiatCurrency);

  const [rate, setRate] = useState<AccountsSellRateResponse>();

  useUpdateAccountsLimits({
    from: fromCurrency,
    to: toCurrency,
    type: AccountsOperationsEnum.fiat_sell,
    payment_method: PaymentAliasEnum.iban_withdraw,
  });

  // useEffect(() => {
  //   if (fromCurrency) {
  //     const routeMap: { [key in PaymentAliasEnum]: string } = {
  //       [PaymentAliasEnum.iban_exchange]: CONST.nav.accountsSellExchange,
  //       [PaymentAliasEnum.iban_invoice]: CONST.nav.accountsSellInvoice,
  //     };
  //     navigate(`${routeMap[paymentAlias]}/${fromCurrency}`);
  //   }
  // }, [fromCurrency]);

  useEffect(() => {
    setFromCurrency(cryptoCurrency);
  }, [cryptoCurrency]);

  useEffect(() => {
    setToCurrency(fiatCurrency);
  }, [fiatCurrency]);

  const config: TConverterOld['config'] = {
    from: { currencies: currencies.crypto, defaultCurrency: cryptoCurrency },
    to: { currencies: [fiatCurrency], defaultCurrency: fiatCurrency },
    displayOptions,
  };

  const updateRate = (res: { data: AccountsSellRateResponse }) => {
    const { total, subtotal } = res.data;
    setRate(res.data);

    return { data: { total, subtotal } };
  };

  const getRate: TConverterOld['getRate'] = async (from, to, amount) => {
    setCurrentState({ from, to, amount });
    return dispatch(
      accountsActions.getAccountSellRate({
        from,
        to,
        amount,
        payment_alias: paymentAlias,
      })
    )
      .unwrap()
      .then(updateRate);
  };

  const updateGetRateOnError = () => {
    const { from, to, amount } = currentState;
    if (from && to && amount) {
      dispatch(
        accountsActions.getAccountSellRate({
          from,
          to,
          amount,
          payment_alias: paymentAlias,
        })
      )
        .unwrap()
        .then(updateRate);
    }
  };

  const onChange: TConverterOld['onChange'] = ({ from, to }) => {
    setFromCurrency(from.currency);
    setToCurrency(to.currency);
    if (!from.amount || !to.amount) setRate(undefined);
  };

  const onSubmit = () => {
    if (rate) {
      dispatch(accountsActions.accountSell({ sell_token: rate.sell_token }))
        .unwrap()
        .then(() => {
          const totalWithCurrency = `${rate?.total[fromCurrency]} ${fromCurrency}`;
          const message = t('notifications.order_processed', { totalWithCurrency });
          dispatch(notificationsActions.add({ message }));

          navigate(CONST.nav.accounts);
        })
        .catch(updateGetRateOnError);
    }
  };

  const isButtonLoading = accounts.loaders.accountSell;
  const isDisabled = !rate?.sell_token;
  const haveFee = rate?.fee?.[fromCurrency];
  const isLoading = global.loaders.getCurrencies;

  return (
    <div className={cn(null, [className])}>
      <div className={cn('container')}>
        <ConverterOld
          config={config}
          getRate={getRate}
          onChange={onChange}
          isLoading={!!accounts.loaders.getAccountSellRate}
          limits={accounts.limits?.transactions_limits}
        />

        {toCurrency && fromCurrency && (
          <RateInformer
            className={cn('rate')}
            type={RateTypesEnum.sell}
            fiat={toCurrency}
            crypto={fromCurrency}
          />
        )}

        <LoaderLayout isLoading={isLoading}>
          {haveFee && (
            <FeeInformer
              amountSubtotal={`${rate?.subtotal?.[toCurrency]}`}
              amountTotal={`${rate?.total?.[toCurrency]}`}
              currency={toCurrency}
              fee={`${rate?.fee?.[toCurrency]}`}
              feeCurrency={toCurrency}
              operationType={CONST.operations.sell}
              currencies={currencies}
              className={cn('informer')}
              bordered
            />
          )}
        </LoaderLayout>

        <div>
          <ButtonOld
            className={cn('button')}
            isLoading={isButtonLoading}
            type="submit"
            onClick={onSubmit}
            disabled={isDisabled}
          >
            {t('sepa.sell_crypto')}
          </ButtonOld>
        </div>
      </div>
    </div>
  );
};

export default AccountsSell;
